import { CountryCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddressAutocomplete,
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Dropdown,
  FormInput,
  Label,
  LabelType,
  SiteTypeDropdown,
} from '../_ui';

import { AnalyticsEvent, captureAnalyticsEvent } from '../../constant/Events';
import { URL_SEARCH_PARAM_KEY } from '../../constant/UrlSearchParams.constant';
import { useAuth } from '../../hooks';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { useAddCompanyOnboardingMutation } from '../../services/company.api';
import { useAddLocationToNewCompanyMutation } from '../../services/location.api';
import { Company, Location, SiteType } from '../../stores/types';
import { CompanyState } from '../../stores/types/company.interface';
import { isSiteTypeValid } from '../../stores/types/sitetype.interface';
import { Form } from '../_ui/Form.component';
import { InputType, ValidationType } from '../_ui/Input.component';
import type { AddressValue } from '../_ui/address/AddressAutocomplete.component';
import { validateEmail } from '../_ui/modal/utils';
import { getCountryList, getPhoneCountryList } from './companyEditor.utils';
import { parseRTKError } from './utils';

export const CreateCompanySelfServeForm = () => {
  const auth = useAuth();
  const hsContactId = useMemo(() => {
    return auth.user?.attributes?.profile?.hsContactId;
  }, [auth.user?.attributes?.profile?.hsContactId]);
  const [_company, setCompany] = useState({
    isoCountryCode: 'CA',
    contactEmail: auth.user?.attributes?.email,
  } as Company);

  const { t } = useTranslation();
  const [hasBeenBlurred, setIsBlurred] = useState(false);

  const [addCompanyOnboarding, addCompanyOnboardingQueryResult] = useAddCompanyOnboardingMutation();
  const [triggerAddLocation] = useAddLocationToNewCompanyMutation();

  const { navigateWithSearchParams } = useNavigateWithSearchParam();

  const commitButtonLabel: string = t('create_company_submit');
  const [phoneCountryCode, setPhoneCountryCode] = useState<string>(() => {
    if (_company.contactPhone) {
      const result = parsePhoneNumber(_company.contactPhone);
      if (result && result?.isValid()) {
        return result.countryCallingCode ? result.countryCallingCode : '1';
      }
    }
    return '1';
  });
  const [phoneNationalNumber, setPhoneNationalNumber] = useState<string>(() => {
    if (_company.contactPhone) {
      const result = parsePhoneNumber(_company.contactPhone);
      if (result && result?.isValid()) {
        return result.nationalNumber ? result.nationalNumber : '';
      }
    }
    return '';
  });

  const [addressGeoData, setAddressGeoData] = useState({
    latitude: '' as string | number,
    longitude: '' as string | number,
    timeZone: '',
  });
  const [siteType, setSiteType] = useState<SiteType | undefined>(undefined);
  const locationCreatedRef = useRef(false);

  // TODO: This validation can't work in this context because it relies on the
  // companies visible to the user; however, at this stage the user has no access
  // to any companies. This needs a more robust backend endpoint that will check
  // for company names across all companies in the system. In the meantime, we
  // will fail on form submission if the name already exists.

  // const [triggerCheckCompanyName, { data: nameCheckResult }] =
  //   useLazyCheckCompanyNameQuery();
  // const doesCompanyNameExist = useMemo(() => {
  //   return nameCheckResult && nameCheckResult.length > 0;
  // }, [nameCheckResult]);

  const countryList = useMemo(() => {
    return getCountryList(_company.isoCountryCode);
  }, [_company.isoCountryCode]);

  const phoneCountryList = useMemo(() => {
    return getPhoneCountryList(_company.isoCountryCode);
  }, [_company.isoCountryCode]);

  const handleSaveClick = () => {
    const payload = {
      ..._company,
      contactPhone: phoneNationalNumber ? `+${phoneCountryCode}${phoneNationalNumber}` : '',
      companyStateList: [
        CompanyState.PENDING_ONBOARDING,
      ] as (CompanyState.PENDING_ONBOARDING | null)[],
    };
    // Track company creation started
    captureAnalyticsEvent(AnalyticsEvent.SELF_SERVE_COMPANY_CREATION_STARTED, {
      hubSpotContactId: hsContactId || undefined,
      companyName: payload.name,
      companyCountry: payload.isoCountryCode,
      userEmail: auth.user?.attributes?.email || null,
    });
    addCompanyOnboarding(payload);
  };

  const isPhoneNumberValid = useMemo(() => {
    return (
      phoneNationalNumber === '' ||
      isValidPhoneNumber(phoneNationalNumber, _company.isoCountryCode as CountryCode)
    );
  }, [phoneNationalNumber, _company.isoCountryCode]);

  const onButtonValidation = () => {
    // empty name
    if (!_company.name || _company.name.length === 0) {
      return false;
    }

    if (!_company.streetNumber || _company.streetNumber.length === 0) {
      return false;
    }

    if (!_company.street || _company.street.length === 0) {
      return false;
    }

    if (!_company.zipOrPostalCode || _company.zipOrPostalCode.length === 0) {
      return false;
    }

    if (!_company.city || _company.city.length === 0) {
      return false;
    }

    if (!_company.stateOrRegion || _company.stateOrRegion.length === 0) {
      return false;
    }

    if (!_company.isoCountryCode || _company.isoCountryCode.length === 0) {
      return false;
    }

    if (
      !_company.contactEmail ||
      _company.contactEmail.length === 0 ||
      !validateEmail(_company.contactEmail)
    ) {
      return false;
    }

    // validate phone number
    if (!isPhoneNumberValid) {
      return false;
    }

    if (!isSiteTypeValid(siteType)) {
      return false;
    }

    return true;
  };

  const handleCompanyChange = (key: string, value: any) => {
    setCompany({
      ..._company,
      [key]: value,
    });
  };

  const handleCompanyNameChange = (value: string) => {
    handleCompanyChange('name', value);
  };

  const handleAddressAutocompleteChange = useCallback((next: Partial<AddressValue>) => {
    setCompany((prev) => ({ ...prev, ...next }));
    if (next.latitude && next.longitude && next.timeZone) {
      setAddressGeoData({
        latitude: next.latitude,
        longitude: next.longitude,
        timeZone: next.timeZone,
      });
    }
  }, []);

  return (
    <Form
      onSubmit={handleSaveClick}
      queryResult={addCompanyOnboardingQueryResult}
      onQuerySuccess={async () => {
        const id = addCompanyOnboardingQueryResult.data?.id;
        if (!id) return;
        if (locationCreatedRef.current) return;
        locationCreatedRef.current = true;

        // Track company creation success
        captureAnalyticsEvent(AnalyticsEvent.SELF_SERVE_COMPANY_CREATION_SUCCESS, {
          hubSpotContactId: hsContactId || undefined,
          companyId: id,
          companyName: _company.name,
          companyCountry: _company.isoCountryCode,
          userEmail: auth.user?.attributes?.email || null,
        });

        // Create location with same address as company
        if (addressGeoData.latitude && addressGeoData.longitude && addressGeoData.timeZone) {
          const locationPayload: Partial<Location> = {
            name: `${_company.streetNumber} ${_company.street}`,
            latitude: Number(addressGeoData.latitude),
            longitude: Number(addressGeoData.longitude),
            timeZone: addressGeoData.timeZone,
            street: _company.street || '',
            streetNumber: _company.streetNumber || '',
            unit: _company.unit || '',
            city: _company.city || '',
            zipOrPostalCode: _company.zipOrPostalCode || '',
            stateOrRegion: _company.stateOrRegion || '',
            isoCountryCode: _company.isoCountryCode || '',
            companyId: id,
            siteType,
          };
          await triggerAddLocation(locationPayload as Location);
        }

        await auth.updateCompanyId(id, false);
        navigateWithSearchParams(
          '/chargers',
          {
            [URL_SEARCH_PARAM_KEY.COMPANY_ID]: id,
          },
          {
            replace: true,
          },
        );
      }}
      onQueryFailed={() => {
        // Track company creation error
        const { user } = auth;
        const { error } = addCompanyOnboardingQueryResult;
        const { errorMessage, errorCode } = parseRTKError(error);
        captureAnalyticsEvent(AnalyticsEvent.SELF_SERVE_COMPANY_CREATION_ERROR, {
          hubSpotContactId: hsContactId || undefined,
          companyName: _company.name,
          companyCountry: _company.isoCountryCode,
          userEmail: user?.attributes?.email || null,
          errorMessage,
          errorCode,
        });
      }}
    >
      <div className='flex flex-col justify-between h-full'>
        <div className='flex flex-col gap-4'>
          <FormInput
            inputId='company_name'
            placeholder={t('company_name')}
            label={t('company_name')}
            onChange={(event) => handleCompanyNameChange(event.target.value)}
            validationType={ValidationType.NOT_EMPTY}
            defaultValue={_company.name}
            width='100%'
            errorLabel={
              _company.name?.length > 0 ? t('company_name_duplicated') : t('company_name_required')
            }
            dataTestId='companyNameInput'
          />

          <div className='flex flex-row gap-4'>
            <div className='flex-1 flex flex-col gap-2'>
              <Label text={t('company_country')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
              <Dropdown
                placement='bottom-end'
                placeholder={t('location_country_placeholder')}
                headerWidth='100%'
                label='name'
                items={countryList}
                onItemClick={(item: any) => {
                  handleCompanyChange('isoCountryCode', item.isoCode);
                  setPhoneCountryCode(item.phonecode);
                  setPhoneNationalNumber('');
                }}
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='hover:bg-grey2 bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
                contentDivHeight={300}
                dataTestId='companyLocationCountry'
              />
            </div>
          </div>

          <div className='flex flex-col gap-2'>
            <AddressAutocomplete
              countryIsoCode={_company.isoCountryCode || 'CA'}
              value={_company}
              onChange={handleAddressAutocompleteChange}
              defaultMode='search'
              include={{ country: false, timezone: false, geo: false }}
              labels={{
                addressLabel: t('company_address'),
                manualToggleLabel: t('location_manual_address'),
                searchPlaceholder: t('location_address_field_prompt'),
                backToSearchLabel: t('search_address'),
                city: t('company_city'),
                state: t('company_state'),
                zip: t('company_zip'),
                streetName: t('company_edit_street_name_holder'),
                streetNumber: t('company_edit_street_number_holder'),
              }}
              stateRequired={['US', 'CA'].includes(_company.isoCountryCode || '')}
            />
          </div>

          <SiteTypeDropdown
            value={siteType}
            onChange={(val: SiteType | undefined) => setSiteType(val)}
            dataTestId='selfServeSiteType'
          />

          <div className='flex flex-col gap-2'>
            <div className='flex flex-row gap-2 items-baseline'>
              <Label
                text={t('company_email')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
                labelHtmlFor='company_email'
              />
            </div>
            <FormInput
              inputId='company_email'
              placeholder={t('company_email_holder')}
              showErrorIcon
              onChange={(event) => handleCompanyChange('contactEmail', event.target.value)}
              validationType={ValidationType.EMAIL}
              defaultValue={_company.contactEmail}
              width='100%'
              dataTestId='companyEmail'
            />
          </div>
          <div className='flex flex-col gap-2'>
            <div className='flex flex-row gap-2 items-baseline'>
              <Label
                text={t('company_phone_number')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
                labelHtmlFor='company_phone_number'
              />
              <Label text={`(${t('optional')})`} type={LabelType.BODY3} color={ColorType.GREY6} />
            </div>
          </div>
          <div className='flex flex-row gap-2'>
            <div style={{ width: '30%' }}>
              <Dropdown
                headerWidth='100%'
                label='flagAndCode'
                items={phoneCountryList}
                onItemClick={(item: any) => {
                  setPhoneCountryCode(item.phonecode);
                  setPhoneNationalNumber('');
                }}
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='hover:bg-grey2 bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
                contentDivHeight={200}
              />
            </div>
            <div style={{ width: '70%' }}>
              <FormInput
                inputId='company_phone_number'
                placeholder={t('placeholder_phone')}
                onChange={(event) => {
                  setPhoneNationalNumber(event.target.value);
                }}
                validationType={ValidationType.NUMBER}
                defaultValue={phoneNationalNumber}
                width='100%'
                inputType={InputType.NUMBER}
                maxLen='16'
                validationQueryResult={hasBeenBlurred && !isPhoneNumberValid}
                errorLabel={t('phone_number_invalid')}
                dataTestId='companyPhoneInput'
                onBlur={() => setIsBlurred(true)}
              />
            </div>
          </div>
          <div className='sticky bottom-0 pt-[16px] pb-[20px] bg-white'>
            <Button
              label={commitButtonLabel}
              type={ButtonType.PRIMARY}
              disabled={!onButtonValidation()}
              size={ButtonSize.SMALL_FULL}
              isSumbit
              dataTestId='saveCompanyButton'
            />
          </div>
        </div>
      </div>
    </Form>
  );
};
